/**
 * WakeWindowTimer
 *
 * Circular timer showing current wake window status.
 * Ring is split into two segments with gaps at both meeting points:
 *   1. Active segment (pp-teal): proportional to minMinutes (safe awake time)
 *   2. Transition segment (pp-orange): proportional to maxMinutes - minMinutes (sleep window)
 * Background tracks are white/15, progress fills in pp-teal and pp-orange.
 * When total time (maxMinutes) is exceeded, both segments turn red.
 */

import { useMemo } from 'react';
import { Moon, Sun, AlertTriangle, Eye, BookOpen } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { type WakeWindowState } from '../lib/wakeWindowCalculator';
import type { SleepPlanMode } from '../lib/sleepScoring';

interface WakeWindowTimerProps {
  state: WakeWindowState;
  babyName: string;
  isBabySleeping: boolean;
  sleepDurationMinutes?: number;
  startTimeFormatted?: string;
  compact?: boolean;
  /** Sleep plan mode – controls visual rendering */
  mode?: SleepPlanMode;
}

export function WakeWindowTimer({
  state,
  babyName,
  isBabySleeping,
  sleepDurationMinutes,
  startTimeFormatted,
  compact = false,
  mode = 'active',
}: WakeWindowTimerProps) {
  const { t } = useTranslation('sleepPlan');

  // SVG circle parameters — doubled stroke width
  const size = compact ? 140 : 200;
  const strokeWidth = compact ? 12 : 16;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // Wake window proportions
  const { minMinutes, maxMinutes } = state.windowRange;
  const tealFraction = minMinutes / maxMinutes;
  const orangeFraction = 1 - tealFraction;

  // Gap between segments (degrees) — must exceed strokeLinecap round-cap overlap
  const gapDeg = 16;
  const tealDeg = tealFraction * 360 - gapDeg;
  const orangeDeg = orangeFraction * 360 - gapDeg;
  const tealArcLength = (tealDeg / 360) * circumference;
  const orangeArcLength = (orangeDeg / 360) * circumference;

  // Segment rotations — offset by half-gap for symmetric gaps at both ends
  const tealRotation = gapDeg / 2;
  const orangeRotation = tealFraction * 360 + gapDeg / 2;

  // Whether total time is exceeded — both segments turn red
  // In observe mode: never show exceeded state. In learn mode: no red alarm.
  const isExceeded = mode === 'active' && state.minutesAwake > maxMinutes;

  // Colors based on mode
  const tealColor = isExceeded ? '#F54D41' : '#0B9488';
  const orangeColor = isExceeded ? '#F54D41' : '#F59E11';

  // Progress fill calculations
  const tealProgress = Math.min(1, state.minutesAwake / minMinutes);
  const orangeProgress = Math.min(1, Math.max(0, (state.minutesAwake - minMinutes) / (maxMinutes - minMinutes)));
  const tealFillArcLength = (isExceeded ? 1 : tealProgress) * tealArcLength;
  const orangeFillArcLength = (isExceeded ? 1 : orangeProgress) * orangeArcLength;

  // Status label — mode-dependent
  const statusLabel = useMemo(() => {
    if (isBabySleeping) {
      return t('dashboard.sleepingSince', { name: babyName, time: startTimeFormatted ?? '' });
    }
    // Observe mode: no status predictions
    if (mode === 'observe') return null;
    // Learn mode: show status but without alarm language
    if (mode === 'learn') {
      switch (state.status) {
        case 'awake':
          return t('dashboard.awakeFor', { name: babyName, minutes: state.minutesAwake });
        case 'early_warning':
          return t('dashboard.nextWindow', { minutes: state.minutesUntilWindow });
        case 'window_open':
          return t('dashboard.windowOpen');
        case 'window_closing':
          return t('dashboard.windowClosing');
        case 'overtired':
          return t('dashboard.windowClosing'); // No "overtired" alarm in learn mode
        default:
          return '';
      }
    }
    // Active mode: full status
    switch (state.status) {
      case 'awake':
        return t('dashboard.awakeFor', { name: babyName, minutes: state.minutesAwake });
      case 'early_warning':
        return t('dashboard.nextWindow', { minutes: state.minutesUntilWindow });
      case 'window_open':
        return t('dashboard.windowOpen');
      case 'window_closing':
        return t('dashboard.windowClosing');
      case 'overtired':
        return t('dashboard.overtired');
      default:
        return '';
    }
  }, [state, babyName, isBabySleeping, startTimeFormatted, t, mode]);

  // Status icon — mode-dependent
  const getStatusIcon = () => {
    if (isBabySleeping) return Moon;
    if (mode === 'observe') return Eye;
    if (mode === 'learn') return BookOpen;
    return isExceeded ? AlertTriangle : Sun;
  };
  const StatusIcon = getStatusIcon();

  // Center icon color — mode-dependent
  const getCenterIconColor = () => {
    if (isBabySleeping) return 'rgba(255,255,255,0.25)';
    if (mode === 'observe') return 'rgba(255,255,255,0.10)';
    if (mode === 'learn') {
      if (state.status === 'window_open' || state.status === 'window_closing') return '#F59E11';
      return '#0B9488';
    }
    // Active
    if (isExceeded) return '#F54D41';
    if (state.status === 'window_open' || state.status === 'window_closing') return '#F59E11';
    return '#0B9488';
  };

  // Observe mode: single unfilled circle
  const isObserve = mode === 'observe' && !isBabySleeping;

  return (
    <div className="flex flex-col items-center">
      {/* Circular Timer */}
      <div className="relative" style={{ width: size, height: size }}>
        <svg
          width={size}
          height={size}
          className="transform -rotate-90"
        >
          {isBabySleeping ? (
            /* Sleeping: full pp-dark ring with filled center */
            <>
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius - strokeWidth / 2}
                fill="rgba(31,41,55,0.25)"
                stroke="none"
              />
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="#1F2937"
                strokeWidth={strokeWidth}
              />
            </>
          ) : isObserve ? (
            /* Observe mode: single unfilled circle in white/10 */
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="rgba(255,255,255,0.10)"
              strokeWidth={strokeWidth}
            />
          ) : (
            <>
              {/* Background segment 1 (teal track) */}
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="rgba(255,255,255,0.15)"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={`${tealArcLength} ${circumference}`}
                transform={`rotate(${tealRotation} ${size / 2} ${size / 2})`}
              />
              {/* Background segment 2 (orange track) */}
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="rgba(255,255,255,0.15)"
                strokeWidth={strokeWidth}
                strokeLinecap="round"
                strokeDasharray={`${orangeArcLength} ${circumference}`}
                transform={`rotate(${orangeRotation} ${size / 2} ${size / 2})`}
              />

              {/* Teal progress fill */}
              {tealFillArcLength > 0 && (
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={tealColor}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={`${tealFillArcLength} ${circumference}`}
                  transform={`rotate(${tealRotation} ${size / 2} ${size / 2})`}
                />
              )}

              {/* Orange progress fill */}
              {orangeFillArcLength > 0 && (
                <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={orangeColor}
                  strokeWidth={strokeWidth}
                  strokeLinecap="round"
                  strokeDasharray={`${orangeFillArcLength} ${circumference}`}
                  transform={`rotate(${orangeRotation} ${size / 2} ${size / 2})`}
                />
              )}
            </>
          )}
        </svg>

        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {isBabySleeping ? (
            <>
              <Moon
                className="mb-1"
                style={{ color: 'rgba(255,255,255,0.25)' }}
                size={compact ? 20 : 28}
              />
              <span className="text-white font-mono text-2xl font-bold">
                {sleepDurationMinutes ?? 0}
              </span>
              <span className="text-white/60 text-xs">min</span>
            </>
          ) : (
            <>
              <StatusIcon
                className="mb-1"
                style={{ color: getCenterIconColor() }}
                size={compact ? 20 : 28}
              />
              <span className="text-white font-mono text-2xl font-bold">
                {state.minutesAwake}
              </span>
              <span className="text-white/60 text-xs">min</span>
            </>
          )}
        </div>
      </div>

      {/* Status label — hidden in observe mode */}
      {statusLabel && (
        <p className="text-white/80 text-sm text-center mt-3 max-w-[250px]">
          {statusLabel}
        </p>
      )}

      {/* Wake window range indicator */}
      {!isBabySleeping && !compact && (
        <div className="flex items-center gap-2 mt-2">
          <span className="text-white/40 text-xs">
            {state.windowRange.minMinutes}
          </span>
          <div className="w-24 h-1.5 rounded-full bg-white/10 relative overflow-hidden">
            {/* No fill in observe mode — just a neutral bar as orientation */}
            {mode !== 'observe' && (
              <div
                className="absolute left-0 top-0 h-full rounded-full transition-all duration-1000"
                style={{
                  width: `${state.progressPercent}%`,
                  backgroundColor: isExceeded ? '#F54D41' : state.status === 'window_open' || state.status === 'window_closing' ? '#F59E11' : '#0B9488',
                }}
              />
            )}
          </div>
          <span className="text-white/40 text-xs">
            {state.windowRange.maxMinutes} min
          </span>
        </div>
      )}
    </div>
  );
}
